import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useMenu from 'hooks/useMenu';
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuizFormMenuItem } from 'store/app/appSelectors';
import { selectBetaQuestionsFeatureEnable, selectEventsFeatureEnable, selectAIModuleFeatureEnable } from 'store/currentUser/currentUserSelectors';
// Models
import { appActions } from 'store/app/appSlice';
import IQuestion from 'models/Question';
import IAnswer from 'models/Answer';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
import PageTypes from 'types/PageTypes';
import TimerTypes from 'types/TimerTypes';
import BannerTypes from 'types/BannerTypes';
// Components
import { Button } from 'components/Buttons';
import Icon from 'components/Icon';
import PageMenuItem  from './MenuItem';
import GenerateQuestionsDialog from 'dialogs/GenerateQuestions.dialog';
import Accordion from 'components/Accordion';
// Icons
import { RiAiGenerate } from "react-icons/ri";
// framer-motion
import { Reorder } from 'framer-motion/dist/framer-motion';
// Utilities
import { expandAccordion } from 'utilities/utilities';

const PagesMenu:FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const quizFormMenuItem = useAppSelector(selectQuizFormMenuItem);
  const betaQuestionsFeatureEnable = useAppSelector(selectBetaQuestionsFeatureEnable);
  const eventsFeatureEnable = useAppSelector(selectEventsFeatureEnable);
  const aiModuleFeatureEnable = useAppSelector(selectAIModuleFeatureEnable);

  const { control, formState:{ errors }, setValue, watch } = useFormContext();

  const watchedQuiz = watch();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'pages'
  });

  const { fields:questionsFields, append:appendQuestion, remove:removeQuestion } = useFieldArray({
    control,
    name: 'questions'
  });

  const { fields:timersFields, append:appendTimer, remove:removeTimer } = useFieldArray({
    control,
    name: 'timers'
  });

  const { fields:bannersFields, append:appendBanner, remove:removeBanner } = useFieldArray({
    control,
    name: 'banners'
  });

  const { fields:waitingsFields, append:appendWaiting, remove:removeWaiting } = useFieldArray({
    control,
    name: 'waitings'
  });

  const [items, setItems] = useState(fields);

  const reorder = () => {
    setValue('pages', items);
  }

  useEffect(() => {
    setItems(fields);
  }, [fields]);

  useEffect(() => {
    expandAccordion(quizFormMenuItem);
  }, [quizFormMenuItem]);

  const initialScore = watchedQuiz.type === QuizTypes.Prediction ? 3000 : 1000;
  const isScoringQuizzes = [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuiz.type);

  const hasPageError = useCallback((ref:string, type:PageTypes) => {
    let fields;
    switch (type) {
      case PageTypes.Banner:
        fields = bannersFields;
        break;
      case PageTypes.Timer:
        fields = timersFields;
        break;
      case PageTypes.Waiting:
        fields = waitingsFields;
        break;
      case PageTypes.Question:
      case PageTypes.AdditionalQuestions:
        fields = questionsFields;
        break;
    }
    const fieldIndex = fields.findIndex((f:any) => f.ref === ref);
    if ( fieldIndex < 0 || !errors || !errors[`${type}s`] ) return false;
    return (errors as any)[`${type}s`][fieldIndex] ? true : false;
    // eslint-disable-next-line
  }, [errors]);

  const hasGameScreenError = errors.banners || errors.timers || errors.questions || errors.additionalQuestions;

  const handleAddQuestion = (pageType: PageTypes, type = QuestionTypes.SingleChoice, title = '', answers = []) => {
    const ref = uuidv4();
    const question:any = {
      ref,
      title,
      description: '',
      type,
      image: '',
      postAnswerImage: '',
      video: '',
      audio: '',
      correctAnswerDescription: '',
      answers,
      timeout: watchedQuiz.type === QuizTypes.Prediction || [QuestionTypes.ConnectionGrid, QuestionTypes.ImagePin, QuestionTypes.TopList].includes(type) ? 30 : 10,
    }
    if (pageType === PageTypes.Question) question['image'] = 'https://media.quizwizards.com/question-default.jpg';
    if (isScoringQuizzes && pageType === PageTypes.Question) question['score'] = initialScore;
    if (watchedQuiz.type === QuizTypes.Prediction) {
      question['scorePlaceholder1'] = 'Score 1';
      question['scorePlaceholder2'] = 'Score 2';
    }
    if (type === QuestionTypes.ImagePin) {
      question['config'] = {
        image: 'https://media.quizwizards.com/image_map_default.png',
        x: 0.5,
        y: 0.5,
        tolerance: 0.08
      };
    }
    
    const uuid = uuidv4();

    appendQuestion(question);

    const page:any = { uuid, refs: [ref], type: pageType };
    if (pageType === PageTypes.AdditionalQuestions) page['title'] = `Survey Question${isScoringQuizzes ? ` - Won't affect score` : ''}`;
    append(page);

    dispatch(appActions.setQuizFormMenuItem(`${pageType}-${uuid}`));
  }

  const handleAddTimer = () => {
    const ref = uuidv4();
    const uuid = uuidv4();
    const timer = { ref, type: TimerTypes.Absolute, time: '', title: '', url: '', image: '' };

    appendTimer(timer);
    append({ uuid, refs: [ref], type: PageTypes.Timer });
    dispatch(appActions.setQuizFormMenuItem(`${PageTypes.Timer}-${uuid}`));
  }

  const handleAddBanner = () => {
    const ref = uuidv4();
    const uuid = uuidv4();
    const banner = { ref, type: BannerTypes.Image, source: '', title: '', image: '', text: '' };

    appendBanner(banner);
    append({ uuid, refs: [ref], type: PageTypes.Banner });
    dispatch(appActions.setQuizFormMenuItem(`${PageTypes.Banner}-${uuid}`));
  }

  const handleAddWaiting = () => {
    const ref = uuidv4();
    const uuid = uuidv4();
    const waiting = { ref, eventId: '', image: '', text: '' };

    appendWaiting(waiting);
    append({ uuid, refs: [ref], type: PageTypes.Waiting });
    dispatch(appActions.setQuizFormMenuItem(`${PageTypes.Waiting}-${uuid}`));
  }

  const handleCopy = (uuid:string) => () => {
    const page:any = fields.find((f:any) => f.uuid === uuid);

    if (!page) return;

    const nextRefs:string[] = [];

    page.refs.forEach((ref:string) => {
      const question = questionsFields.find((f:any) => f.ref === ref);
      const timer = timersFields.find((f:any) => f.ref === ref);
      const banner = bannersFields.find((f:any) => f.ref === ref);

      const nextRef = uuidv4();
      nextRefs.push(nextRef);

      if (question) {
        const { title, type, answers, score, image, video, audio, format, scorePlaceholder1, scorePlaceholder2 } = question as any;
    
        const nextQuestion:any = {
          ref: nextRef,
          title,
          description: '',
          type,
          image,
          video,
          audio,
          answers: answers.map((answer:IAnswer) => ({ ...answer, ref: uuidv4() })),
        }
        if (isScoringQuizzes) nextQuestion['score'] = score || initialScore;
        if (format) nextQuestion['format'] = format;
        if (scorePlaceholder1) nextQuestion['scorePlaceholder1'] = scorePlaceholder1;
        if (scorePlaceholder2) nextQuestion['scorePlaceholder2'] = scorePlaceholder2;
        
        appendQuestion(nextQuestion);
      }
  
      if (timer) {
        const { type, time, title, url, image } = timer as any;
    
        const nextTimer = { ref: nextRef, type, time, title, url, image };
  
        appendTimer(nextTimer);
      }
  
      if (banner) {
        const { type, source, title } = banner as any;
    
        const nextBanner = { ref: nextRef, type, source, title };
  
        appendBanner(nextBanner);
      }
    })

    const nextUuid = uuidv4();

    append({ uuid: nextUuid, refs: nextRefs, type: page.type });
    dispatch(appActions.setQuizFormMenuItem(`${page.type}-${nextUuid}`));
  }

  const handleDelete = (uuid:string) => () => {
    const pageFieldIndex = fields.findIndex((f:any) => f.uuid === uuid);

    if (pageFieldIndex === -1) return;

    const page:any = fields[pageFieldIndex];

    page.refs.forEach((ref:string) => {
      if ( [PageTypes.Question, PageTypes.AdditionalQuestions].includes(page.type) ) {
        const questionFieldIndex = questionsFields.findIndex((f:any) => f.ref === ref);
        if (questionFieldIndex !== -1) removeQuestion(questionFieldIndex);
      }

      if (page.type === PageTypes.Banner) {
        const bannerFieldIndex = bannersFields.findIndex((f:any) => f.ref === ref);
        if (bannerFieldIndex !== -1) removeBanner(bannerFieldIndex);
      }

      if (page.type === PageTypes.Timer) {
        const timerFieldIndex = timersFields.findIndex((f:any) => f.ref === ref);
        if (timerFieldIndex !== -1) removeTimer(timerFieldIndex);
      }

      if (page.type === PageTypes.Waiting) {
        const waitingFieldIndex = waitingsFields.findIndex((f:any) => f.ref === ref);
        if (waitingFieldIndex !== -1) removeWaiting(waitingFieldIndex);
      }
    })

    remove(pageFieldIndex);
  }

  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();

  const handleAddQuestions = (questions:IQuestion[]) => {
    questions.forEach((question) => {
      const answers:any = question.answers.map(answer => ({ ...answer, ref: uuidv4() }));
      handleAddQuestion(PageTypes.Question, question.type, question.title, answers);
    });
  }

  const pageTypeLabel:Record<PageTypes, string> = {
    [PageTypes.Banner]: t('labels.interstitial'),
    [PageTypes.Question]: t('labels.question'),
    [PageTypes.AdditionalQuestions]: t('labels.additionalQuestion'),
    [PageTypes.Timer]: t('labels.timer'),
    [PageTypes.Waiting]: t('labels.waiting'),
  }

  const questionsPages = items.filter((page:any) => page.type === PageTypes.Question);
  const getQuestionPageIndex = (uuid:string) => questionsPages.findIndex((questionPage:any) => questionPage.uuid === uuid);

  const allowNewPagesTypes = betaQuestionsFeatureEnable
    && [QuizTypes.BeatTheExpert, QuizTypes.Scoring, QuizTypes.Streak].includes(watchedQuiz.type);

  return (
    <Fragment>
      {open ? (
        <GenerateQuestionsDialog open={open} onClose={toggle} handleAddQuestions={handleAddQuestions} />
      ) : null}

      <Menu>
        {![QuizTypes.Survey, QuizTypes.TeamSelector].includes(watchedQuiz.type) ? (
          <MenuItem onClick={() => handleAddQuestion(PageTypes.Question)}>{t('labels.question')}</MenuItem>
        ) : null}
        {allowNewPagesTypes ? [
          <MenuItem
            key="connectionsGrid"
            onClick={() => handleAddQuestion(PageTypes.Question, QuestionTypes.ConnectionGrid)}>{t('labels.connectionsGrid')}</MenuItem>,
          <MenuItem
            key="ranking"
            onClick={() => handleAddQuestion(PageTypes.Question, QuestionTypes.Ranking)}>{t('labels.ranking')}</MenuItem>,
          <MenuItem
            key="topList"
            onClick={() => handleAddQuestion(PageTypes.Question, QuestionTypes.TopList)}>{t('labels.topList')}</MenuItem>,
          <MenuItem
            key="imagePin"
            onClick={() => handleAddQuestion(PageTypes.Question, QuestionTypes.ImagePin)}>{t('labels.imagePin')}</MenuItem>,
        ] : null}
        <MenuItem onClick={() => handleAddQuestion(PageTypes.AdditionalQuestions)}>{t('labels.additionalQuestion')}</MenuItem>
        <MenuItem onClick={handleAddBanner}>{t('labels.interstitial')}</MenuItem>
        {eventsFeatureEnable ? (
          <MenuItem onClick={handleAddWaiting}>{t('labels.waiting')}</MenuItem>
        ) : null}
        {watchedQuiz.type !== QuizTypes.Survey ? (
          <MenuItem onClick={handleAddTimer}>{t('labels.timer')}</MenuItem>
        ) : null}
      </Menu>

      <Accordion label={t('pages.quizForm.preGameScreens')} size="small">
        <div className="w-full">
          <PageMenuItem
            menuItem={{
              label: t('pages.quizForm.promoVideoLabel'),
              value: 'promoVideo',
              icon: 'video'
            }}
            active={quizFormMenuItem === 'promoVideo'}
          />
          <PageMenuItem
            menuItem={{
              label: t('pages.quizForm.landingPageLabel'),
              value: 'landingPage',
              icon: 'list'
            }}
            active={quizFormMenuItem === 'landingPage'}
          />
          <PageMenuItem
            menuItem={{
              label: t('pages.quizForm.waiverLabel'),
              value: 'waiver',
              icon: 'checkmark'
            }}
            active={quizFormMenuItem === 'waiver'}
          />
          {watchedQuiz.type !== QuizTypes.Survey ? (
            <PageMenuItem
              menuItem={{
                label: t('pages.quizForm.brandVideoLabel'),
                value: 'brandVideo',
                icon: 'video'
              }}
              active={quizFormMenuItem === 'brandVideo'}
            />
          ) : null}
          {watchedQuiz.registrationPage?.showPriorQuiz && (
            <PageMenuItem
              menuItem={{
                label: t('pages.quizForm.registrationLabel'),
                value: 'registration',
                icon: 'user_check'
              }}
              active={quizFormMenuItem === 'registration'}
            />
          )}
          {watchedQuiz.type !== QuizTypes.Survey ? (
            <PageMenuItem
              menuItem={{
                label: t('pages.quizForm.countdownLabel'),
                value: 'countdown',
                icon: 'stopwatch'
              }}
              active={quizFormMenuItem === 'countdown'}
            />
          ) : null}
        </div>
      </Accordion>

      <Accordion label={t('pages.quizForm.gameScreen')} size="small" error={hasGameScreenError} defaultExpanded>
        <div className="w-full">
          <div className="mb-2">
            <Button
              className="flex justify-center items-center w-full"
              onClick={openMenu}
              variant="contained"
            >
              <Icon className="mr-2" icon="add" />
              {t('pages.quizForm.addPageButton')}
            </Button>
            {aiModuleFeatureEnable && ![QuizTypes.Survey, QuizTypes.TeamSelector].includes(watchedQuiz.type) ? (
              <Button
                className="flex justify-center items-center w-full mt-2"
                onClick={toggle}
                variant="contained"
              >
                <RiAiGenerate className="mr-2" fontSize={20} />
                {t('pages.quizForm.aiGeneratedQuestionsButton')}
              </Button>
            ) : null}
          </div>

          <Reorder.Group
            axis="y"
            values={items}
            as="div"
            onReorder={setItems}
            className="flex flex-col flex-grow my-2 overflow-y-scroll"
          >
            {items.map((field:any) => {
              const isDraggable = field.type !== PageTypes.Question || watchedQuiz.type !== QuizTypes.TeamSelector;

              return (
                <Reorder.Item
                  key={`${field.type}-item-${field.id}`}
                  value={field}
                  as="div"
                  drag={isDraggable}
                  onDragEnd={isDraggable ? reorder : undefined}
                >
                  <PageMenuItem
                    menuItem={{
                      label: `${pageTypeLabel[field.type as PageTypes]} ${
                        field.type === PageTypes.Question ? getQuestionPageIndex(field.uuid) + 1 : ''
                      }`,
                      value: `${field.type}-${field.uuid}`,
                    }}
                    onDelete={isDraggable ? handleDelete(field.uuid) : undefined}
                    onCopy={isDraggable ? handleCopy(field.uuid) : undefined}
                    active={quizFormMenuItem === `${field.type}-${field.uuid}`}
                    error={hasPageError(field.refs[0], field.type)}
                    dragIcon={isDraggable}
                  />
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        </div>
      </Accordion>

      <Accordion label={t('pages.quizForm.postGameScreens')} size="small" hidden={watchedQuiz.type === QuizTypes.Personality && watchedQuiz.registrationPage?.showPriorQuiz}>
        <div className="w-full">
          {[QuizTypes.Scoring, QuizTypes.Streak].includes(watchedQuiz.type) && (
            <PageMenuItem
              menuItem={{
                label: t('pages.quizForm.scoringResultLabel'),
                value: 'scoringResult',
                icon: 'cup'
              }}
              active={quizFormMenuItem === 'scoringResult'}
            />
          )}
          {!watchedQuiz.registrationPage?.showPriorQuiz && (
            <PageMenuItem
              menuItem={{
                label: t('pages.quizForm.registrationLabel'),
                value: 'registration',
                icon: 'user_check'
              }}
              active={quizFormMenuItem === 'registration'}
            />
          )}
          {[QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.Poll, QuizTypes.Survey, QuizTypes.RankingPoll, QuizTypes.TeamSelector, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuiz.type) && (
            <PageMenuItem
              menuItem={{
                label: [QuizTypes.Survey, QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector].includes(watchedQuiz.type) ? t('pages.quizForm.postQuizPageLabel') : t('pages.quizForm.callToActionLabel'),
                value: 'callToAction',
                icon: 'call_to_action'
              }}
              active={quizFormMenuItem === 'callToAction'}
            />
          )}
          {[QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuiz.type) && (
            <PageMenuItem
              menuItem={{
                label: [QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector].includes(watchedQuiz.type) ? t('pages.quizForm.resultsLabel') : t('pages.quizForm.leaderboardLabel'),
                value: 'results',
                icon: 'medal'
              }}
              active={quizFormMenuItem === 'results'}
            />
          )}
          {/* {watchedQuiz.type === QuizTypes.Personality && (
            <PageMenuItem
              menuItem={{
                label: t('pages.quizForm.resultPageLabel'),
                value: 'results',
                icon: 'medal'
              }}
              active={false}
              clicable={false}
            />
          )} */}
        </div>
      </Accordion>
    </Fragment>
  )
}

export default PagesMenu;